import type { ReactNode } from 'react';
import { css, type Theme } from '@emotion/react';
import { Shade, Skeleton } from 'client/components/Skeleton';
import { Typography } from 'client/components/Typography';
import { layoutStyles, sideWidth, gutterWidth, mainWidth } from 'client/Theme';

export const SectionSkeleton = () => {
  return (
    <Shade>
      <Section title={<Skeleton width="30%" animate={false} />} subtitle={<Skeleton width="90%" animate={false} />} css={skeletonStyle}>
        <Skeleton type="rect" height={56} animate={false} />
        <Skeleton type="rect" height={56} animate={false} />
        <Skeleton type="rect" height={56} animate={false} />
      </Section>
    </Shade>
  );
};

export interface SectionProps {
  title?: string | JSX.Element;
  subtitle?: string | JSX.Element;
  inert?: boolean;
  children?: ReactNode;
}

export const Section = ({ title, subtitle, inert, children, ...other }: SectionProps) => {
  return (
    <section
      // @ts-ignore
      inert={inert ? '' : null}
      css={sectionStyle}
      {...other}
    >
      <header>
        {!!title && <Typography variant="h6">{title}</Typography>}
        {!!subtitle && <Typography>{subtitle}</Typography>}
      </header>
      <div css={contentStyle}>{children}</div>
    </section>
  );
};

export interface ActionsProps {
  inert?: boolean;
  children?: ReactNode;
}

export const Actions = ({ inert, children, ...other }: ActionsProps) => {
  return (
    <section
      // @ts-ignore
      inert={inert ? '' : null}
      css={actionsStyle}
      {...other}
    >
      {children}
    </section>
  );
};

export interface SettingsProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> {}

export const Settings = ({ children, ...props }: SettingsProps) => {
  return <article {...props}>{children}</article>;
};

const sectionStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  ${layoutStyles}
  margin-bottom: 2rem;

  &[inert] {
    opacity: 0.3;
  }

  > header {
    margin-bottom: 1rem;

    .typography {
      &.h6 {
        margin-bottom: 0.5rem;
      }
    }

    p {
      color: ${theme.color.grey[100]};
    }
  }

  ${theme.mediaQuery.medium(css`
    max-width: ${mainWidth}ch;
    margin-bottom: 4rem;
  `)}

  ${theme.mediaQuery.large(css`
    flex-direction: row;
    padding-top: 4rem;
    ${layoutStyles}
    margin-bottom: 4rem;

    &:not(:first-of-type) {
      border-top: 2px solid ${theme.color.grey[60]};
    }

    > header {
      flex-shrink: 1;
      margin-right: ${gutterWidth}ch;
      width: ${sideWidth}ch;
    }

    > div {
      width: ${mainWidth}ch;
    }
  `)}
`;

const contentStyle = (theme: Theme) => css`
  .MuiFormControl-root {
    margin-bottom: 1rem;
  }

  > hr {
    margin: 2rem 0;
    height: 2px;
    background-color: ${theme.color.grey[60]};
    border: none;
  }
`;

const actionsStyle = (theme: Theme) => css`
  display: flex;
  ${layoutStyles}
  justify-content: right;
  align-items: center;
  padding-top: 64px;
  margin-bottom: 128px;

  &[inert] {
    opacity: 0.3;
  }

  &:not(:first-of-type) {
    border-top: 2px solid ${theme.color.grey[60]};
  }
`;

const skeletonStyle = (theme: Theme) => css`
  > div > div {
    margin-bottom: 1rem;
    border-radius: ${theme.borderRadius}px;
  }
`;
